import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, X, ChevronRight, Users, FileCheck, CreditCard, TrendingUp, Trophy, Medal, Award, Target, BarChart3, Mail, Eye, Download, Send, KeyRound, Globe } from "lucide-react";
import { COUNTRY_PREFIXES, normalizeCountryCode } from "../constants/countries";
import { API } from "../config";
import { PasswordInput } from "../components/shared/PasswordInput";

const StatCard = ({ label, value, icon: Icon, color = "text-lojack-red", bg = "bg-red-500/10" }) => (
  <div className="bg-[#111] border border-gray-800 rounded-xl p-4" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-center gap-3">
      <div className={`w-9 h-9 rounded-lg ${bg} flex items-center justify-center`}><Icon size={18} className={color} /></div>
      <div><p className="text-2xl font-bold text-white">{value}</p><p className="text-gray-500 text-xs">{label}</p></div>
    </div>
  </div>
);

const ProgressBar = ({ current, target }) => {
  if (!target || target <= 0) return <span className="text-gray-600 text-xs">Nessun obiettivo</span>;
  const pct = Math.min(Math.round((current / target) * 100), 150);
  const color = pct >= 100 ? 'bg-lojack-blue-500' : pct >= 70 ? 'bg-lojack-clause-500' : 'bg-red-500';
  return (
    <div className="flex items-center gap-2 min-w-[120px]">
      <div className="flex-1 bg-gray-800 rounded-full h-2 overflow-hidden">
        <div className={`h-full rounded-full ${color} transition-all`} style={{ width: `${Math.min(pct, 100)}%` }} />
      </div>
      <span className={`text-xs font-mono ${pct >= 100 ? 'text-lojack-blue-400' : pct >= 70 ? 'text-lojack-clause-400' : 'text-red-400'}`}>{pct}%</span>
    </div>
  );
};

const MedalIcon = ({ position }) => {
  if (position === 1) return <span className="text-lg" title="Oro">&#129351;</span>;
  if (position === 2) return <span className="text-lg" title="Argento">&#129352;</span>;
  if (position === 3) return <span className="text-lg" title="Bronzo">&#129353;</span>;
  return <span className="text-gray-500 text-sm font-mono">#{position}</span>;
};

const DealerDetailModal = ({ dealerId, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');

  useEffect(() => {
    const fetchPerformance = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/dashboard/dealers/${dealerId}/performance`);
        setData(res.data);
      } catch { toast.error("Errore caricamento dati dealer"); }
      finally { setLoading(false); }
    };
    fetchPerformance();
  }, [dealerId]);

  if (loading) return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[200] flex items-center justify-center" onClick={onClose}>
      <div className="spinner" />
    </div>
  );
  if (!data) return null;

  const { dealer, stats, sellers, leaderboard, month } = data;
  const tabs = [
    { id: 'overview', label: 'Panoramica', icon: BarChart3 },
    { id: 'sellers', label: `Venditori (${sellers.length})`, icon: Users },
    { id: 'leaderboard', label: 'Classifica', icon: Trophy },
  ];

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[200] flex items-center justify-center p-4" style={{width:'100vw',height:'100vh',top:0,left:0}} onClick={onClose} data-testid="dealer-detail-modal">
      <div className="bg-[#0D0D0D] rounded-2xl w-full max-w-4xl max-h-[90vh] border border-gray-700 overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-800 shrink-0">
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-lg font-bold ${dealer.is_active ? 'bg-lojack-blue-500/20 text-lojack-blue-400' : 'bg-red-500/20 text-red-400'}`}>
              {(dealer.first_name && dealer.last_name)
                ? `${dealer.first_name[0]}${dealer.last_name[0]}`
                : (dealer.company_name || dealer.email || 'D').slice(0, 2).toUpperCase()}
            </div>
            <div>
              <h2 className="text-xl font-bold text-white" data-testid="dealer-detail-name">{
                (dealer.first_name && dealer.last_name)
                  ? `${dealer.first_name} ${dealer.last_name}`
                  : (dealer.company_name || dealer.email)
              }</h2>
              <p className="text-gray-500 text-sm">
                {dealer.company_name} | {dealer.city} | <span className="font-mono text-gray-400">{dealer.dealer_code}</span>
                {dealer.member_number && <span className="ml-2 text-lojack-clause-400 font-mono">· {dealer.member_number}</span>}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a href={`${API}/dashboard/dealers/${dealerId}/report-pdf`} target="_blank" rel="noreferrer" className="flex items-center gap-2 px-3 py-2 bg-lojack-red/10 text-lojack-red rounded-lg text-sm font-medium hover:bg-lojack-red/20 transition-colors" data-testid="download-pdf-btn"><Download size={16} /> Scarica PDF</a>
            <button onClick={onClose} className="p-2 hover:bg-gray-800 rounded-lg" data-testid="close-dealer-detail"><X size={20} className="text-gray-400" /></button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-800 px-5 shrink-0">
          {tabs.map(tb => (
            <button key={tb.id} onClick={() => setTab(tb.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${tab === tb.id ? 'border-lojack-red text-white' : 'border-transparent text-gray-500 hover:text-gray-300'}`}
              data-testid={`tab-${tb.id}`}>
              <tb.icon size={16} /> {tb.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="overflow-y-auto p-5 flex-1">
          {tab === 'overview' && (
            <div className="space-y-5 animate-fade-in">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <StatCard label="Clienti Totali" value={stats.total_customers} icon={Users} />
                <StatCard label="Contratti Firmati" value={stats.total_contracts} icon={FileCheck} color="text-lojack-blue-400" bg="bg-lojack-blue-500/10" />
                <StatCard label="Card Generate" value={stats.total_cards} icon={CreditCard} color="text-blue-400" bg="bg-blue-500/10" />
                <StatCard label="Email Inviate" value={stats.emails_sent} icon={Mail} color="text-purple-400" bg="bg-purple-500/10" />
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <StatCard label="Questo Mese" value={stats.this_month} icon={TrendingUp} color="text-lojack-clause-400" bg="bg-lojack-clause-500/10" />
                <StatCard label="Ultimi 7gg" value={stats.last_7_days} icon={TrendingUp} color="text-cyan-400" bg="bg-cyan-500/10" />
                <StatCard label="Ultimi 30gg" value={stats.last_30_days} icon={TrendingUp} color="text-orange-400" bg="bg-orange-500/10" />
                <StatCard label="Clienti Diretti" value={stats.direct_customers} icon={Users} color="text-gray-400" bg="bg-gray-500/10" />
              </div>

              {stats.monthly_target > 0 && (
                <div className="bg-[#111] border border-gray-800 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-gray-400 text-sm flex items-center gap-2"><Target size={16} className="text-lojack-red" /> Obiettivo Mensile Globale</p>
                    <span className="text-white font-bold">{stats.this_month} / {stats.monthly_target}</span>
                  </div>
                  <ProgressBar current={stats.this_month} target={stats.monthly_target} />
                </div>
              )}

              <div className="bg-[#111] border border-gray-800 rounded-xl p-4">
                <p className="text-gray-400 text-sm mb-3">Riepilogo Rete</p>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div><p className="text-2xl font-bold text-white">{sellers.length}</p><p className="text-gray-500 text-xs">Venditori</p></div>
                  <div><p className="text-2xl font-bold text-lojack-blue-400">{sellers.filter(s => s.is_active).length}</p><p className="text-gray-500 text-xs">Attivi</p></div>
                  <div><p className="text-2xl font-bold text-blue-400">{sellers.filter(s => s.password_set).length}</p><p className="text-gray-500 text-xs">Account Attivati</p></div>
                </div>
              </div>
            </div>
          )}

          {tab === 'sellers' && (
            <div className="space-y-3 animate-fade-in">
              {sellers.length === 0 ? (
                <p className="text-gray-500 text-center py-8">Nessun venditore registrato per questo dealer.</p>
              ) : (
                <>
                  <div className="hidden sm:grid grid-cols-12 gap-2 px-3 py-2 text-gray-500 text-xs font-medium uppercase">
                    <div className="col-span-3">Venditore</div>
                    <div className="col-span-2">Clienti</div>
                    <div className="col-span-2">Contratti</div>
                    <div className="col-span-1">7gg</div>
                    <div className="col-span-1">30gg</div>
                    <div className="col-span-3">Obiettivo Mese</div>
                  </div>
                  {sellers.map(s => (
                    <div key={s.id} className="bg-[#111] border border-gray-800 rounded-xl p-3 sm:grid sm:grid-cols-12 sm:gap-2 sm:items-center" data-testid={`seller-row-${s.id}`}>
                      <div className="col-span-3 flex items-center gap-3 mb-2 sm:mb-0">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold ${s.is_active ? 'bg-blue-500/20 text-blue-400' : 'bg-gray-700 text-gray-500'}`}>
                          {s.first_name[0]}{s.last_name[0]}
                        </div>
                        <div>
                          <p className="text-white text-sm font-medium">{s.first_name} {s.last_name}</p>
                          <p className="text-gray-600 text-xs">{s.department || s.email}</p>
                        </div>
                      </div>
                      <div className="col-span-2"><span className="text-white font-bold">{s.total_customers}</span><span className="text-gray-600 text-xs ml-1">totali</span></div>
                      <div className="col-span-2"><span className="text-lojack-blue-400 font-bold">{s.contracts_signed}</span><span className="text-gray-600 text-xs ml-1">firmati</span></div>
                      <div className="col-span-1 text-cyan-400 text-sm">{s.last_7_days}</div>
                      <div className="col-span-1 text-orange-400 text-sm">{s.last_30_days}</div>
                      <div className="col-span-3">
                        <div className="flex items-center gap-2">
                          <span className="text-white text-sm font-mono">{s.customers_this_month}/{s.monthly_target || 0}</span>
                          <ProgressBar current={s.customers_this_month} target={s.monthly_target} />
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}

          {tab === 'leaderboard' && (
            <div className="space-y-4 animate-fade-in">
              <div className="flex items-center justify-between">
                <h3 className="text-white font-bold flex items-center gap-2"><Trophy size={18} className="text-lojack-clause-400" /> Classifica Mensile — {month}</h3>
                <span className="text-gray-500 text-xs">{leaderboard.length} venditori</span>
              </div>
              {leaderboard.length === 0 ? (
                <p className="text-gray-500 text-center py-8">Nessun venditore attivo questo mese.</p>
              ) : (
                <div className="space-y-2">
                  {leaderboard.map((entry, i) => {
                    const isTop3 = entry.position <= 3;
                    const bgGradient = entry.position === 1 ? 'bg-gradient-to-r from-lojack-clause-500/10 to-transparent border-lojack-clause-500/30' :
                      entry.position === 2 ? 'bg-gradient-to-r from-gray-400/10 to-transparent border-gray-400/30' :
                      entry.position === 3 ? 'bg-gradient-to-r from-lojack-clause-700/10 to-transparent border-lojack-clause-700/30' :
                      'bg-[#111] border-gray-800';
                    return (
                      <div key={entry.id} className={`border rounded-xl p-4 flex items-center gap-4 ${bgGradient}`} data-testid={`leaderboard-${entry.position}`}>
                        <div className="w-10 text-center"><MedalIcon position={entry.position} /></div>
                        <div className="flex-1">
                          <p className="text-white font-medium">{entry.first_name} {entry.last_name}</p>
                          {entry.department && <p className="text-gray-600 text-xs">{entry.department}</p>}
                        </div>
                        <div className="text-right">
                          <p className="text-white font-bold text-lg">{entry.customers_this_month}</p>
                          <p className="text-gray-500 text-xs">clienti</p>
                        </div>
                        <div className="text-right min-w-[60px]">
                          <p className="text-lojack-blue-400 font-bold">{entry.contracts_this_month}</p>
                          <p className="text-gray-500 text-xs">contratti</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const DealersPage = () => {
  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  // Annex A 28/05/2026 — Dealer Manager account form. The `password`
  // field is removed: the backend generates a temporary password
  // and emails it to the dealer's personal email (forced change at
  // first login). `first_name` and `last_name` remain optional —
  // they're not in the Annex A template but identify the human
  // referent who receives the welcome email.
  const emptyForm = {
    company_name: '', address: '', zip_code: '', city: '',
    phone: '', dealer_email: '', country: 'IT',
    email: '', first_name: '', last_name: '', member_number: '',
  };
  const [form, setForm] = useState(emptyForm);
  const [creating, setCreating] = useState(false);
  const [selectedDealerId, setSelectedDealerId] = useState(null);

  const fetchDealers = async () => { setLoading(true); try { const res = await axios.get(`${API}/dashboard/dealers`); setDealers(res.data); } catch { toast.error('Errore caricamento dealer'); } finally { setLoading(false); } };
  useEffect(() => { fetchDealers(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault(); setCreating(true);
    try {
      const res = await axios.post(`${API}/dashboard/dealers`, form);
      toast.success(`Dealer creato · ${res.data.member_number} · email di benvenuto inviata`);
      setShowAdd(false); setForm(emptyForm); fetchDealers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    } finally { setCreating(false); }
  };

  const toggleActive = async (d) => { try { await axios.put(`${API}/dashboard/dealers/${d.id}`, { is_active: !d.is_active }); toast.success(d.is_active ? 'Dealer disattivato' : 'Dealer attivato'); fetchDealers(); } catch { toast.error('Errore'); } };
  const deleteDealer = async (d) => { if (!window.confirm(`Eliminare il dealer ${d.first_name || d.company_name || d.email}?`)) return; try { await axios.delete(`${API}/dashboard/dealers/${d.id}`); toast.success('Dealer eliminato'); fetchDealers(); } catch { toast.error('Errore'); } };

  // Admin actions (30/05/2026 Fabrizio request): resend the original
  // welcome email (when the dealer has not yet completed first login)
  // or rotate the password generating a fresh temporary credential
  // (any time, for forgot-password scenarios where the dealer cannot
  // use the self-service flow).
  const resendWelcome = async (d) => {
    if (!window.confirm(`Reinviare la mail di benvenuto a ${d.email}?\n\nVerra' generata una nuova password temporanea e inviata via email al dealer (la vecchia smettera' di funzionare).`)) return;
    try {
      await axios.post(`${API}/dashboard/dealers/${d.id}/resend-welcome-email`);
      toast.success('Email di benvenuto rinviata correttamente');
      fetchDealers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore invio email');
    }
  };
  const resetDealerPassword = async (d) => {
    const ok = window.confirm(
      `Resettare la password del dealer ${d.email}?\n\n` +
      `Verra' generata una NUOVA password temporanea e inviata via email. ` +
      `La password attuale del dealer smettera' di funzionare immediatamente. ` +
      `Il dealer dovra' cambiarla al prossimo accesso.\n\n` +
      `Procedere?`
    );
    if (!ok) return;
    try {
      await axios.post(`${API}/dashboard/dealers/${d.id}/reset-password`);
      toast.success('Password resettata. Una nuova password temporanea e\' stata inviata via email.');
      fetchDealers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore reset password');
    }
  };

  // Inline country editor — fix for legacy dealers created before the
  // "country" field was made part of the Annex A flow. Without a
  // country, the dealer's customer-form starts with IT defaults and
  // shows the wrong language/format hints (Polish dealer sees Italian
  // PESEL placeholder — Fabrizio's screenshot 30/05/2026).
  const [editingCountryId, setEditingCountryId] = useState(null);
  const [pendingCountry, setPendingCountry] = useState("");
  const openCountryEditor = (d) => {
    setEditingCountryId(d.id);
    setPendingCountry(d.country || "IT");
  };
  const saveCountry = async () => {
    if (!editingCountryId || !pendingCountry) { setEditingCountryId(null); return; }
    try {
      await axios.put(`${API}/dashboard/dealers/${editingCountryId}`, { country: pendingCountry });
      toast.success(`Paese aggiornato a ${pendingCountry}`);
      setEditingCountryId(null);
      fetchDealers();
    } catch {
      toast.error('Errore aggiornamento paese');
    }
  };

  // ── Bulk country backfill (30/05/2026) ────────────────────────────
  // For installs with several legacy dealers we expose a dry-run +
  // apply flow that infers the country from phone prefix / postal
  // code shape / email TLD. The admin sees a preview table before
  // confirming so there are zero "surprise" updates.
  const [backfillOpen, setBackfillOpen] = useState(false);
  const [backfillPreview, setBackfillPreview] = useState(null);
  const [backfillBusy, setBackfillBusy] = useState(false);
  const dealersMissingCountry = dealers.filter(d => !d.country).length;

  const openBackfill = async () => {
    setBackfillOpen(true);
    setBackfillBusy(true);
    try {
      const r = await axios.post(`${API}/admin/dealers/backfill-country?dry_run=true`);
      setBackfillPreview(r.data);
    } catch {
      toast.error('Errore caricamento anteprima');
    } finally {
      setBackfillBusy(false);
    }
  };
  const applyBackfill = async () => {
    const willUpdate = (backfillPreview?.would_update || []).length;
    if (willUpdate === 0) { setBackfillOpen(false); return; }
    if (!window.confirm(`Confermi l'aggiornamento di ${willUpdate} dealer? L'operazione e' tracciata in audit log e puo' essere rivista in caso di errori.`)) return;
    setBackfillBusy(true);
    try {
      const r = await axios.post(`${API}/admin/dealers/backfill-country?dry_run=false`);
      toast.success(`Backfill completato: ${r.data.summary.applied} dealer aggiornati`);
      setBackfillOpen(false);
      setBackfillPreview(null);
      fetchDealers();
    } catch {
      toast.error('Errore esecuzione backfill');
    } finally {
      setBackfillBusy(false);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto animate-fade-in" data-testid="dealers-page">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="text-3xl font-bold text-white">Gestione Dealer</h1><p className="text-gray-400 text-sm mt-1">{dealers.length} dealer registrati | Portale: <a href="/dealer" target="_blank" className="text-lojack-red hover:underline">/dealer</a></p></div>
        <div className="flex items-center gap-2">
          {dealersMissingCountry > 0 && (
            <button
              onClick={openBackfill}
              className="px-4 py-2 bg-lojack-clause-500/10 hover:bg-lojack-clause-500/20 border border-lojack-clause-500/40 text-lojack-clause-300 rounded-xl text-sm font-medium flex items-center gap-2"
              data-testid="backfill-country-btn"
              title="Inferisce il paese dei dealer legacy dal telefono / CAP / email"
            >
              <Globe size={14} /> Backfill paesi <span className="bg-lojack-clause-500/30 px-1.5 rounded-full text-[10px]">{dealersMissingCountry}</span>
            </button>
          )}
          <button onClick={() => setShowAdd(true)} className="btn-lojack flex items-center gap-2" data-testid="add-dealer-btn"><Plus size={18} /> Nuovo Dealer</button>
        </div>
      </div>

      {loading ? <div className="flex justify-center py-12"><div className="spinner"></div></div> : (
        <div className="space-y-3">
          {dealers.map(d => {
            const initials = (d.first_name && d.last_name)
              ? `${d.first_name[0]}${d.last_name[0]}`
              : (d.company_name || d.email || 'D').slice(0, 2).toUpperCase();
            const displayName = (d.first_name && d.last_name)
              ? `${d.first_name} ${d.last_name}`
              : (d.company_name || d.email);
            return (
            <div key={d.id} className="wallet-card flex flex-col sm:flex-row sm:items-center justify-between gap-3 cursor-pointer hover:border-gray-600 transition-colors" onClick={() => setSelectedDealerId(d.id)} data-testid={`dealer-card-${d.id}`}>
              <div className="flex items-center gap-4">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold ${d.is_active ? 'bg-lojack-blue-500/20 text-lojack-blue-400' : 'bg-red-500/20 text-red-400'}`}>{initials}</div>
                <div>
                  <p className="text-white font-medium">{displayName}</p>
                  <p className="text-gray-500 text-xs">
                    {d.email} | {d.company_name || '-'} | {d.city || '-'}
                    {d.member_number && <span className="ml-2 text-lojack-clause-400 font-mono">· {d.member_number}</span>}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3" onClick={e => e.stopPropagation()}>
                <span className="text-gray-400 text-xs font-mono">{d.dealer_code}</span>
                {/* Country badge — click to edit. Missing country is
                    a hard signal of legacy data; we highlight it amber
                    so admins fix it before the dealer's UI breaks. */}
                <button
                  onClick={() => openCountryEditor(d)}
                  title={d.country ? `Paese dealer: ${d.country} — click per modificare` : "PAESE MANCANTE — click per impostare"}
                  className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${d.country ? "bg-gray-800/60 hover:bg-gray-700 text-gray-300" : "bg-lojack-clause-500/20 hover:bg-lojack-clause-500/30 text-lojack-clause-300 animate-pulse"}`}
                  data-testid={`country-badge-${d.id}`}
                >
                  {d.country
                    ? `${(COUNTRY_PREFIXES.find(c => c.code === normalizeCountryCode(d.country)) || {}).flag || "🌍"} ${d.country}`
                    : (<><Globe size={12} /> Manca</>)}
                </button>
                <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded-full">{d.customers_count || 0} clienti</span>
                {/* Welcome email resend — only available BEFORE first login.
                    After the dealer has personalised the password,
                    the admin must use Reset Password instead. */}
                {d.must_change_password && (
                  <button
                    onClick={() => resendWelcome(d)}
                    className="text-gray-500 hover:text-lojack-blue-400 transition-colors"
                    title="Re-invia email di benvenuto (dealer non ha ancora effettuato il primo login)"
                    data-testid={`resend-welcome-${d.id}`}
                  >
                    <Send size={16} />
                  </button>
                )}
                <button
                  onClick={() => resetDealerPassword(d)}
                  className="text-gray-500 hover:text-lojack-clause-400 transition-colors"
                  title="Reset password (genera una nuova password temporanea e la invia via email)"
                  data-testid={`reset-password-${d.id}`}
                >
                  <KeyRound size={16} />
                </button>
                <button onClick={() => toggleActive(d)} className={`px-3 py-1 rounded-full text-xs ${d.is_active ? 'bg-lojack-blue-500/20 text-lojack-blue-400' : 'bg-red-500/20 text-red-400'}`} data-testid={`toggle-dealer-${d.id}`}>{d.is_active ? 'Attivo' : 'Disattivato'}</button>
                <button onClick={() => deleteDealer(d)} className="text-gray-600 hover:text-red-400 transition-colors" data-testid={`delete-dealer-${d.id}`}><Trash2 size={16} /></button>
                <ChevronRight size={16} className="text-gray-600" />
              </div>
            </div>
            );
          })}
        </div>
      )}

      {showAdd && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[200] flex items-center justify-center p-4" style={{width:'100vw',height:'100vh',top:0,left:0}} onClick={() => setShowAdd(false)}>
          <div className="bg-[#1A1A1A] rounded-2xl w-full max-w-2xl border border-gray-700 overflow-hidden max-h-[92vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-gray-700 shrink-0">
              <div>
                <h2 className="text-lg font-bold text-white" data-testid="add-dealer-title">Nuovo Dealer</h2>
                <p className="text-gray-500 text-xs mt-0.5">Conforme Annex A — Dealer Manager account generation</p>
              </div>
              <button onClick={() => setShowAdd(false)} className="p-2 hover:bg-gray-800 rounded-lg"><X size={20} className="text-gray-400" /></button>
            </div>
            <form onSubmit={handleCreate} className="flex flex-col flex-1 min-h-0" data-testid="dealer-form">
              <div className="flex-1 min-h-0 overflow-y-auto p-5 space-y-5" data-testid="dealer-form-scroll">

              {/* Dati Azienda Dealer (Annex A — finiscono nel contratto) */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lojack-clause-300 text-[10px] font-bold uppercase tracking-wider">Dati azienda dealer (Annex A)</h3>
                  <span className="text-gray-600 text-[10px]">campi stampati nel contratto cliente</span>
                </div>
                <div className="bg-[#0d0d0d] border border-lojack-clause-500/20 rounded-xl p-4 space-y-3">
                  <div><label className="text-gray-400 text-xs">Ragione sociale*</label><input value={form.company_name} onChange={e => setForm({...form, company_name: e.target.value})} className="w-full mt-1 px-4 py-2.5 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-lojack-red focus:outline-none" required data-testid="dealer-form-company" /></div>
                  <div><label className="text-gray-400 text-xs">Indirizzo*</label><input value={form.address} onChange={e => setForm({...form, address: e.target.value})} className="w-full mt-1 px-4 py-2.5 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-lojack-red focus:outline-none" required data-testid="dealer-form-address" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="text-gray-400 text-xs">CAP*</label><input value={form.zip_code} onChange={e => setForm({...form, zip_code: e.target.value})} className="w-full mt-1 px-4 py-2.5 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-lojack-red focus:outline-none" required data-testid="dealer-form-zip" /></div>
                    <div><label className="text-gray-400 text-xs">Città*</label><input value={form.city} onChange={e => setForm({...form, city: e.target.value})} className="w-full mt-1 px-4 py-2.5 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-lojack-red focus:outline-none" required data-testid="dealer-form-city" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="text-gray-400 text-xs">Telefono azienda*</label><input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="+39 02 1234567" className="w-full mt-1 px-4 py-2.5 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-lojack-red focus:outline-none" required data-testid="dealer-form-phone" /></div>
                    <div><label className="text-gray-400 text-xs">Email aziendale*</label><input type="email" value={form.dealer_email} onChange={e => setForm({...form, dealer_email: e.target.value})} placeholder="info@nomesocieta.it" className="w-full mt-1 px-4 py-2.5 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-lojack-red focus:outline-none" required data-testid="dealer-form-dealer-email" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-gray-400 text-xs">Paese</label>
                      <select value={form.country} onChange={e => setForm({...form, country: e.target.value})} className="w-full mt-1 px-4 py-2.5 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-lojack-red focus:outline-none" data-testid="dealer-form-country">
                        <option value="IT">🇮🇹 Italia</option>
                        <option value="GB">🇬🇧 Regno Unito</option>
                        <option value="IRL">🇮🇪 Irlanda</option>
                        <option value="DE">🇩🇪 Germania</option>
                        <option value="AT">🇦🇹 Austria</option>
                        <option value="CH">🇨🇭 Svizzera</option>
                        <option value="FR">🇫🇷 Francia</option>
                        <option value="BE">🇧🇪 Belgio</option>
                        <option value="ES">🇪🇸 Spagna</option>
                        <option value="PT">🇵🇹 Portogallo</option>
                        <option value="NL">🇳🇱 Paesi Bassi</option>
                        <option value="PL">🇵🇱 Polonia</option>
                        <option value="SE">🇸🇪 Svezia</option>
                        <option value="DK">🇩🇰 Danimarca</option>
                        <option value="NO">🇳🇴 Norvegia</option>
                        <option value="FI">🇫🇮 Finlandia</option>
                        <option value="CZ">🇨🇿 Repubblica Ceca</option>
                        <option value="GR">🇬🇷 Grecia</option>
                      </select>
                    </div>
                    <div><label className="text-gray-400 text-xs">Member Number <span className="text-gray-600">(auto)</span></label><input value={form.member_number} onChange={e => setForm({...form, member_number: e.target.value})} placeholder="LJ-XXXXX (auto)" className="w-full mt-1 px-4 py-2.5 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-lojack-red focus:outline-none font-mono" data-testid="dealer-form-member-number" /></div>
                  </div>
                </div>
              </div>

              {/* Dati referente (account login portale dealer) */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lojack-blue-300 text-[10px] font-bold uppercase tracking-wider">Account referente (portale dealer)</h3>
                  <span className="text-gray-600 text-[10px]">credenziali inviate via mail al primo login</span>
                </div>
                <div className="bg-[#0d0d0d] border border-lojack-blue-500/20 rounded-xl p-4 space-y-3">
                  <div><label className="text-gray-400 text-xs">Email personale referente* <span className="text-gray-600">(per credenziali di accesso)</span></label><input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="nome.cognome@nomesocieta.it" className="w-full mt-1 px-4 py-2.5 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-lojack-red focus:outline-none" required data-testid="dealer-form-email" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="text-gray-400 text-xs">Nome <span className="text-gray-600">(opz.)</span></label><input value={form.first_name} onChange={e => setForm({...form, first_name: e.target.value})} className="w-full mt-1 px-4 py-2.5 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-lojack-red focus:outline-none" data-testid="dealer-form-firstname" /></div>
                    <div><label className="text-gray-400 text-xs">Cognome <span className="text-gray-600">(opz.)</span></label><input value={form.last_name} onChange={e => setForm({...form, last_name: e.target.value})} className="w-full mt-1 px-4 py-2.5 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-lojack-red focus:outline-none" data-testid="dealer-form-lastname" /></div>
                  </div>
                </div>
              </div>

              {/* Info: password autogenerata */}
              <div className="flex items-start gap-2 text-xs text-gray-400 bg-[#0a0a0a] border border-gray-800 rounded-xl p-3" data-testid="dealer-form-password-info">
                <span className="text-lojack-clause-400 shrink-0 mt-0.5">ℹ</span>
                <span>Il sistema genera automaticamente una <strong className="text-white">password temporanea</strong> e la invia via email al referente nella lingua del Paese selezionato. Il dealer dovrà cambiarla al primo accesso.</span>
              </div>

              </div>
              {/* Sticky footer with submit button — always visible regardless of form scroll position */}
              <div className="border-t border-gray-700 bg-[#1A1A1A] p-4 shrink-0 flex items-center justify-end gap-3" data-testid="dealer-form-footer">
                <button type="button" onClick={() => setShowAdd(false)} className="px-4 py-2.5 text-gray-400 hover:text-white text-sm font-medium transition-colors" data-testid="dealer-form-cancel">Annulla</button>
                <button type="submit" disabled={creating} className="btn-lojack flex items-center justify-center gap-2 disabled:opacity-50 px-6" data-testid="dealer-form-submit">{creating ? 'Creazione…' : 'Crea Dealer e invia credenziali'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {selectedDealerId && <DealerDetailModal dealerId={selectedDealerId} onClose={() => setSelectedDealerId(null)} />}

      {/* Inline country editor modal. Compact dialog with a single
          dropdown (all 18 supported countries) — fixes the legacy
          dealers that pre-date the Annex A 'country' field. */}
      {editingCountryId && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[300] flex items-center justify-center p-4" onClick={() => setEditingCountryId(null)} data-testid="edit-country-modal">
          <div className="bg-[#1A1A1A] rounded-2xl border border-gray-700 w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-white mb-1">Paese del dealer</h3>
            <p className="text-gray-500 text-xs mb-4">
              Determina la lingua di default del form "Nuovo Cliente", il prefisso telefonico iniziale e il formato Codice Fiscale/P.IVA suggerito al dealer.
            </p>
            <select
              value={pendingCountry}
              onChange={e => setPendingCountry(e.target.value)}
              className="w-full bg-[#0a0a0a] border border-gray-700 rounded-xl px-3 py-2.5 text-white focus:border-lojack-blue-500 outline-none"
              data-testid="edit-country-select"
            >
              {COUNTRY_PREFIXES.map(c => (
                <option key={c.code} value={c.code}>{c.flag} {c.code} — {c.name}</option>
              ))}
            </select>
            <div className="flex items-center justify-end gap-2 mt-5">
              <button onClick={() => setEditingCountryId(null)} className="px-4 py-2 text-gray-400 hover:text-white text-sm">Annulla</button>
              <button onClick={saveCountry} className="btn-lojack px-5" data-testid="edit-country-save">Salva</button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk country backfill — dry-run preview + apply */}
      {backfillOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[300] flex items-center justify-center p-4" onClick={() => !backfillBusy && setBackfillOpen(false)} data-testid="backfill-modal">
          <div className="bg-[#1A1A1A] rounded-2xl border border-gray-700 w-full max-w-3xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-800">
              <h3 className="text-lg font-bold text-white">Backfill paese dealer legacy</h3>
              <p className="text-gray-500 text-xs mt-1">
                Inferisce il paese di ogni dealer privo di valore dal numero di telefono / codice postale / TLD email. Anteprima dry-run — niente viene scritto finchè non clicchi "Applica".
              </p>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {backfillBusy && !backfillPreview && (
                <p className="text-gray-500 text-center py-8">Generazione anteprima...</p>
              )}
              {backfillPreview && (
                <>
                  <div className="grid grid-cols-3 gap-3 mb-5 text-sm">
                    <div className="bg-[#0a0a0a] border border-gray-800 rounded-xl p-3">
                      <p className="text-gray-500 text-[10px] uppercase tracking-wider">Totale legacy</p>
                      <p className="text-2xl font-bold">{backfillPreview.total_legacy_dealers}</p>
                    </div>
                    <div className="bg-lojack-blue-500/10 border border-lojack-blue-500/40 rounded-xl p-3">
                      <p className="text-lojack-blue-400 text-[10px] uppercase tracking-wider">Auto-fixabili</p>
                      <p className="text-2xl font-bold text-lojack-blue-300">{backfillPreview.summary.would_update}</p>
                    </div>
                    <div className="bg-gray-800/30 border border-gray-700 rounded-xl p-3">
                      <p className="text-gray-500 text-[10px] uppercase tracking-wider">Da fixare a mano</p>
                      <p className="text-2xl font-bold text-gray-400">{backfillPreview.summary.unresolved}</p>
                    </div>
                  </div>

                  {backfillPreview.would_update.length > 0 && (
                    <div className="mb-5" data-testid="backfill-would-update-table">
                      <h4 className="text-sm font-semibold text-white mb-2">Dealer che verranno aggiornati</h4>
                      <div className="bg-[#0a0a0a] border border-gray-800 rounded-xl overflow-hidden">
                        <table className="w-full text-xs">
                          <thead className="bg-gray-900/50 text-gray-500 text-[10px] uppercase">
                            <tr><th className="px-3 py-2 text-left">Dealer</th><th className="px-3 py-2 text-center">→</th><th className="px-3 py-2 text-left">Paese inferito</th><th className="px-3 py-2 text-left">Evidenza</th></tr>
                          </thead>
                          <tbody>
                            {backfillPreview.would_update.map(item => (
                              <tr key={item.dealer_id} className="border-t border-gray-800/60">
                                <td className="px-3 py-2 text-gray-300">{item.name}<br/><span className="text-gray-500 text-[10px]">{item.email}</span></td>
                                <td className="px-3 py-2 text-center text-gray-600">→</td>
                                <td className="px-3 py-2"><span className="text-lojack-blue-300 font-semibold">{(COUNTRY_PREFIXES.find(c => c.code === normalizeCountryCode(item.inferred)) || {}).flag} {item.inferred}</span></td>
                                <td className="px-3 py-2 text-gray-500 text-[11px]">{item.evidence}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {backfillPreview.unresolved.length > 0 && (
                    <div data-testid="backfill-unresolved-table">
                      <h4 className="text-sm font-semibold text-gray-400 mb-2">Da fixare a mano con il badge sulla riga</h4>
                      <div className="bg-[#0a0a0a] border border-gray-800 rounded-xl overflow-hidden">
                        <table className="w-full text-xs">
                          <tbody>
                            {backfillPreview.unresolved.map(item => (
                              <tr key={item.dealer_id} className="border-t border-gray-800/60">
                                <td className="px-3 py-2 text-gray-400">{item.name}<br/><span className="text-gray-500 text-[10px]">{item.email}</span></td>
                                <td className="px-3 py-2 text-gray-500 text-[11px] italic">{item.reason}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="p-4 border-t border-gray-800 flex justify-end gap-3">
              <button onClick={() => setBackfillOpen(false)} disabled={backfillBusy} className="px-4 py-2 text-gray-400 hover:text-white text-sm disabled:opacity-50">Chiudi</button>
              <button
                onClick={applyBackfill}
                disabled={backfillBusy || !backfillPreview || backfillPreview.summary.would_update === 0}
                className="btn-lojack px-5 disabled:opacity-50"
                data-testid="backfill-apply-btn"
              >
                {backfillBusy ? '…' : `Applica (${backfillPreview?.summary?.would_update || 0})`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DealersPage;
